/**
 * 资产 picker 列表项（角色 / 物品 / 场景共用）
 *
 * 整行点击切换选中（多选场景）；右侧放大镜按钮单独触发预览浮层。
 * 缩略图缺失时回退到首字母彩底（与原视觉保持一致）。
 *
 * 形状区分：
 *   - character：48px 圆形（sky）
 *   - prop：48px 圆角矩形（amber）
 *   - scene：48px 圆角矩形（emerald）
 */

import { Check, Eye } from 'lucide-react';
import { withAuthToken } from '../../../../utils/storageUrl';

interface Props {
  kind: 'character' | 'prop' | 'scene';
  selected: boolean;
  name: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  onToggleSelect: () => void;
  onPreview: () => void;
}

export function AssetPickerListItem({
  kind,
  selected,
  name,
  description,
  thumbnailUrl,
  onToggleSelect,
  onPreview,
}: Props) {
  // 选中态描边色（按 kind 区分）
  const ringClass = selected
    ? kind === 'character'
      ? 'border-sky-500 bg-sky-50 shadow-sm shadow-sky-100'
      : kind === 'prop'
        ? 'border-amber-500 bg-amber-50 shadow-sm shadow-amber-100'
        : 'border-emerald-500 bg-emerald-50 shadow-sm shadow-emerald-100'
    : 'border-slate-200 bg-white hover:border-slate-300';

  const placeholderClass =
    kind === 'character'
      ? 'bg-sky-100 text-sky-700'
      : kind === 'prop'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-emerald-100 text-emerald-700';

  // 角色用圆形，物品/场景用圆角矩形
  const shapeClass = kind === 'character' ? 'rounded-full' : 'rounded-xl';

  const initialChar = name.trim().slice(0, 1) || '?';

  return (
    <div
      className={`group flex w-full items-center gap-3 rounded-2xl border p-2.5 transition ${ringClass}`}
    >
      {/* 缩略图 */}
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden text-base font-semibold ${shapeClass} ${placeholderClass} ring-1 ring-slate-100`}
      >
        {thumbnailUrl ? (
          <img
            src={withAuthToken(thumbnailUrl)}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <span>{initialChar}</span>
        )}
      </div>

      {/* 文本（点击区域） */}
      <button
        type="button"
        onClick={onToggleSelect}
        className="min-w-0 flex-1 cursor-pointer text-left"
      >
        <div className="truncate text-sm font-semibold text-slate-900">{name}</div>
        {description ? (
          <div className="truncate text-xs text-slate-500">{description}</div>
        ) : (
          <div className="truncate text-xs italic text-slate-400">（无描述）</div>
        )}
      </button>

      {/* 预览按钮（独立点击，不触发选中） */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPreview();
        }}
        className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        title="预览"
      >
        <Eye className="h-4 w-4" />
      </button>

      {/* 选中徽标 */}
      {selected && (
        <div
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full shadow-md ${
            kind === 'character'
              ? 'bg-sky-500'
              : kind === 'prop'
                ? 'bg-amber-500'
                : 'bg-emerald-500'
          }`}
        >
          <Check className="h-3 w-3 text-white" />
        </div>
      )}
    </div>
  );
}
